import _ from 'lodash';
import React from 'react';
import Helmet from 'react-helmet';
import {Link, useParams} from 'react-router-dom';

import ActivityCalendar from '../../components/ActivityCalendar';
import Egg from '../../components/Egg';
import {ExpandableProjectListHeader} from '../../components/ExpandableProjectList';
import ImageIconHeader from '../../components/ImageIconHeader';
import NoMatch from '../../components/NoMatch';
import {PageWithSidebar} from '../../components/PageWithSidebar';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ProfileSidebar} from '../../components/ProfileSidebar';
import ProjectsTableUser, {
  ProjectsTableHeader,
} from '../../components/ProjectsTableUser';
import ReportShowcase from '../../components/ReportShowcase';
import ReportsTable, {ReportsTableReport} from '../../components/ReportsTable';
import RoutedTab from '../../components/RoutedTab';
import RunsTable, {RunsTableRun} from '../../components/RunsTable';
import {Section, SectionHeader} from '../../components/Section';
import {TeamSettings} from '../../components/TeamSettings';
import {InstrumentedLoader as Loader} from '../../components/utility/InstrumentedLoader';
import DeferredQueryData from '../../containers/DeferredQueryData';
import {
  UserAccountType,
  useUserRunsActivityQuery,
  useUserRunsQuery,
} from '../../generated/graphql';
import {Project} from '../../graphql/projects';
import {useQuery} from '../../state/graphql/query';
import {Graph, Team, UserInfo} from '../../types/graphql';
import {useAdminModeActive, viewerUsingAdminPrivileges} from '../../util/admin';
import {setDocumentTitle} from '../../util/document';
import {useEntitySettings} from '../../util/entitySettings';
import {propagateErrorsContext} from '../../util/errors';
import {isPublicRead} from '../../util/projectAccess';
import {
  canonicalize,
  profilePage,
  reportGallery,
  setMetaDescription,
} from '../../util/urls';
import {CreateReportPrompt} from './../../components/Reports';
import {useCreateReportInProject} from './../../components/Reports/CreateReportPrompt/useCreateReportInProject';
import {useCreateReportPromptProjectData} from './../../components/Reports/CreateReportPrompt/useCreateReportPromptProjectData';
import {PROFILE_PAGE_QUERY} from './../../graphql/profilePage';
import {useViewer} from './../../state/viewer/hooks';
import {LoadableProjectList} from './LoadableProjectList';
import * as S from './ProfilePage.styles';

interface ProfilePageProps {
  filePath: string;
  tab?: string;
}

interface ProfilePageQueryVariables {
  username: string;
}

export interface ProfilePageQueryData {
  user: {
    username: string;
    photoUrl: string;
    name?: string;
    email?: string;
    userInfo?: UserInfo;
    hideTeamsFromPublic: boolean;
    id: string;
    accountType: string;
    projects: Graph<Project>;
    views: Graph<ReportsTableReport>;
    starredViews: Graph<ReportsTableReport>;
    teams: Graph<Team>;
  };
}

const ProfilePageComp = (props: ProfilePageProps) => {
  const {entityName: username} = useParams<{entityName: string}>();
  const viewer = useViewer();
  const showSettingsTab = useAdminModeActive();

  const profilePageQuery = useQuery<
    ProfilePageQueryData,
    ProfilePageQueryVariables
  >(PROFILE_PAGE_QUERY, {
    variables: {username},
  });

  const userRunsActivityQuery = useUserRunsActivityQuery({
    variables: {
      username,
      dailyRunCountLimit: getDailyRunCountLimit(),
    },
    context: propagateErrorsContext(),
  });

  const userRunsQuery = useUserRunsQuery({
    variables: {username},
    context: propagateErrorsContext(),
  });

  const {entitySettings} = useEntitySettings(username);

  const hideActivityCalendar =
    entitySettings?.[`hideActivityCalendar`] ?? false;

  const data = profilePageQuery.loading ? null : profilePageQuery.data?.user;

  React.useEffect(() => {
    // meta description tag for google search results
    if (data) {
      if (username) {
        setDocumentTitle(getTitle(data.name ?? data.username), false);
      }
      const {name, userInfo} = data;
      const descParts: string[] = [];
      if (name) {
        descParts.push(`View ML projects from ${name} on Weights & Biases.`);
      }
      if (userInfo != null) {
        const {bio, company, location} = userInfo;
        if (bio) {
          descParts.push(`${bio}.`);
        }
        if (company && location) {
          descParts.push(`Working at ${company} in ${location}.`);
        } else if (company) {
          descParts.push(`Working at ${company}.`);
        } else if (location) {
          descParts.push(`Working in ${location}.`);
        }
      }
      if (descParts.length > 0) {
        setMetaDescription(descParts.join(' '));
      }
    }
  }, [data, username]);

  const projectWithEnoughRuns = useCreateReportPromptProjectData(username);
  const createReportInProject = useCreateReportInProject('Profile page');
  if (profilePageQuery.loading) {
    return <Loader name="profile-page-loader" />;
  }

  if (data == null) {
    return <Loader name="null-data-loader" />;
  }

  if (data.accountType === UserAccountType.Anonymous) {
    return <NoMatch />;
  }

  const isOwnPage =
    data.username === viewer?.username &&
    data.accountType !== UserAccountType.Anonymous;

  const editable =
    isOwnPage || Boolean(viewer?.admin && viewerUsingAdminPrivileges());

  const showLikesTab = data.starredViews?.edges?.length > 0 || isOwnPage;

  const userHasPublicReports = data.views?.edges?.some(({node: r}) =>
    isPublicRead(r.project)
  );

  const promptUserToShowcase = editable && userHasPublicReports;

  const showcasedReports = _.sortBy(
    data.views?.edges?.map(e => e.node)?.filter(r => r.showcasedAt != null),
    r => (r.showcasedAt != null ? new Date(r.showcasedAt) : new Date())
  );

  return (
    <PageWithSidebar
      sidebar={
        <>
          <Helmet>
            <meta property="og:type" content="profile" />
            <meta
              property="og:url"
              content={canonicalize(window.location.href)}
            />
            <meta
              property="og:title"
              content={getTitle(data.name ?? data.username)}
            />
            <meta property="og:image" content={data.photoUrl} />
            {data.userInfo?.bio && (
              <meta property="og:description" content={data.userInfo.bio} />
            )}
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:creator" content={data.username} />
            <link rel="canonical" href={canonicalize(window.location.href)} />
          </Helmet>
          <ProfileSidebar data={data} isEditable={editable} />
        </>
      }>
      <>
        <S.ProfilePageContent>
          <RoutedTab
            data-test="profile-page-tabs"
            baseUrl={profilePage(data.username)}
            tabSlugs={[
              'overview',
              'projects',
              ...(showLikesTab ? ['stars'] : []),
              ...(showSettingsTab ? ['settings'] : []),
            ]}
            activeTabSlug={props.tab}
            panes={[
              {
                menuItem: 'Overview',
                render: () => (
                  <>
                    {(promptUserToShowcase || showcasedReports.length > 0) && (
                      <Section>
                        <ReportShowcase
                          inProfilePage
                          items={showcasedReports}
                          readOnly={!editable}
                        />
                      </Section>
                    )}
                    <Section>
                      <SectionHeader>
                        <ExpandableProjectListHeader
                          entityName={data.username}
                          isReadOnly={!editable}
                        />
                      </SectionHeader>
                      <LoadableProjectList editable={editable} />
                    </Section>

                    {!hideActivityCalendar && (
                      <Section>
                        <SectionHeader>
                          <ImageIconHeader icon="activity" text="Activity" />
                        </SectionHeader>
                        <DeferredQueryData
                          name="profile-page-activity-calendar"
                          query={userRunsActivityQuery}>
                          {runsData => (
                            <ActivityCalendar
                              dailyRunCount={
                                runsData?.user?.dailyRunCount ?? []
                              }
                              suppressHeader
                            />
                          )}
                        </DeferredQueryData>
                      </Section>
                    )}

                    <Section>
                      <SectionHeader>
                        <ImageIconHeader icon="run" text="Runs" />
                      </SectionHeader>
                      <DeferredQueryData
                        name="profile-page-runs-table"
                        query={userRunsQuery}>
                        {runsData => {
                          const runs = runsData?.user?.runs ?? {edges: []};
                          return (
                            <RunsTable
                              data={runs as unknown as Graph<RunsTableRun>}
                              name="Runs"
                              showUserColumn={false}
                              suppressHeader
                            />
                          );
                        }}
                      </DeferredQueryData>
                    </Section>

                    <Section>
                      <SectionHeader>
                        <ImageIconHeader icon="report" text="Reports" />
                      </SectionHeader>
                      {data.views.edges.length > 0 && (
                        <ReportsTable
                          data={data.views}
                          pageSize={10}
                          showShowcase={editable}
                          suppressHeader
                        />
                      )}
                      {data.views.edges.length === 0 &&
                        projectWithEnoughRuns && (
                          <CreateReportPrompt
                            entityName={projectWithEnoughRuns?.entityName}
                            projectName={projectWithEnoughRuns?.name}
                            location="Profile Page"
                            onCreateReport={() => {
                              createReportInProject(
                                username,
                                projectWithEnoughRuns.name
                              );
                            }}
                          />
                        )}
                    </Section>
                  </>
                ),
              },
              {
                menuItem: 'Projects',
                render: () => (
                  <Section>
                    <SectionHeader>
                      <ProjectsTableHeader
                        entityName={data.username}
                        readOnly={!editable}
                      />
                    </SectionHeader>
                    <ProjectsTableUser
                      entityName={data.username}
                      readOnly={!editable}
                      suppressHeader
                    />
                  </Section>
                ),
              },
              ...(showLikesTab
                ? [
                    {
                      menuItem: 'Likes',
                      render: () =>
                        data.starredViews.edges.length > 0 ? (
                          <ReportsTable
                            data={data.starredViews}
                            pageSize={20}
                          />
                        ) : (
                          <div className="no-stars-watermark">
                            You haven’t liked any reports yet. Check out{' '}
                            <Link to={reportGallery()}>Fully Connected</Link>{' '}
                            for curated reports on popular machine learning
                            research topics.
                          </div>
                        ),
                    },
                  ]
                : []),
              ...(showSettingsTab
                ? [
                    {
                      menuItem: 'Settings',
                      render: () => <TeamSettings entityName={data.username} />,
                    },
                  ]
                : []),
            ]}
          />
        </S.ProfilePageContent>
        <Egg />
      </>
    </PageWithSidebar>
  );
};

export const ProfilePage = React.memo(ProfilePageComp);

export default ProfilePage;

function getTitle(user: string): string {
  return `${user} - Machine Learning Portfolio in Weights & Biases`;
}

function getDailyRunCountLimit(): number {
  // Fills activity calendar with a year of data,
  // with some adjustment for alignment reasons.
  return 7 * 51 + 1 + new Date().getDay();
}
